// badge.ts — the number on the app icon.
//
// A banner interrupts; a badge waits. For a console somebody keeps open all
// day behind other windows, the badge is the honest signal: it says "there is
// something here" without demanding attention this second, and it is still
// true an hour later when they look at the Dock.
//
// COUNTS UNREAD CONVERSATIONS, not messages. Six texts from one person is one
// thing to deal with, and a badge reading 6 would send somebody hunting for
// five conversations that do not exist.
//
// Only meaningful once the app is installed — in a browser tab the platform
// has nowhere to draw it — but calling it there is harmless, so there is no
// standalone check to get wrong.

/** Feature-detected once; absent on Safari and older browsers. */
function supported(): boolean {
  return typeof navigator !== 'undefined' && 'setAppBadge' in navigator
}

/**
 * Show `count`, or clear the badge when it is zero.
 *
 * Never throws and never reports: a badge is a nicety, and a failure to draw
 * one must not disturb anything the person is actually doing.
 */
export function setBadge(count: number): void {
  if (!supported()) return
  const nav = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>
    clearAppBadge?: () => Promise<void>
  }
  try {
    if (count > 0) void nav.setAppBadge?.(count)?.catch(() => {})
    else void nav.clearAppBadge?.()?.catch(() => {})
  } catch {
    // Some platforms throw synchronously when the app is not installed.
  }
}

/** How many conversations are waiting on this person. */
export function unreadCount(threads: ReadonlyArray<{ unread?: boolean }>): number {
  return threads.reduce((n, t) => (t.unread ? n + 1 : n), 0)
}
